import React from "react";
import { t } from "ttag";

import { Svg, TooltipIcon, legacyTooltipAttrs } from "../svg";

import styles from "./CompareAdapt2.module.scss";

/**
 * Native port of ``cms/blocks/hc-block/hc-block--compare-adapt-2.html`` (#33404).
 *
 * Class-driven structural compare table; imagery is CSS background-image keyed
 * off element classes (``top-bg``/``middle-adapt``/``tempur-technology__icon``/
 * etc.). The ``__react_component_tooltip`` static markup is reproduced VERBATIM
 * (a global JS tooltip lib rebinds to it). Unlike the other compare tables this
 * one uses DISTINCT tooltip ids (``tooltip-7``/``tooltip-9``/``tooltip-0``/…),
 * preserved exactly. The legacy ``{% include 'cms/components/svg.html' %}``
 * includes become the shared ``Svg``/``TooltipIcon`` inline components.
 */

// Per-instance tooltip with the distinct ``data-for`` / ``id`` this table uses.
const Tooltip = (props: { id: string; text: string }): React.JSX.Element => (
    <>
        <span data-tip="true" data-for={props.id} {...legacyTooltipAttrs}>
            <Svg
                Icon={TooltipIcon}
                src="tooltip-icon"
                className="tooltip__handle"
            />
        </span>
        <div
            className="__react_component_tooltip place-top type-dark tooltip__content"
            id={props.id}
            role="tooltip"
            data-id="tooltip"
            style={{ maxWidth: "275px" }}
        >
            {props.text}
        </div>
    </>
);

export const CompareAdapt2 = (): React.JSX.Element => (
    <div className={styles.largeWidth}>
        <header>
            <p className={styles.preText}>Adapt Mattress Collection</p>
            <h2>
                Compare TEMPUR-Adapt<sup>®</sup>
            </h2>
            <p>
                Superior pressure relief and motion cancellation for all-night
                comfort and support
            </p>
        </header>
        <div>
            <table cellSpacing="0" cellPadding="0">
                <thead>
                    <tr>
                        <th scope="col">
                            <span className="ada-screenreader-only">
                                {t`Mattress Models`}
                            </span>
                        </th>
                        <th scope="col">
                            <div className={styles.headerContainer}>
                                <span>
                                    TEMPUR<sup>®</sup>
                                </span>
                                <span
                                    className={
                                        styles.headerContainerWithTooltip
                                    }
                                >
                                    Technology{" "}
                                    <span
                                        className="tooltip"
                                        data-tooltip="One-of-a-kind technology providing the exceptional support, pressure relief, and motion cancellation that made Tempur-Pedic® famous."
                                        title="One-of-a-kind technology providing the exceptional support, pressure relief, and motion cancellation that made Tempur-Pedic® famous."
                                    >
                                        <Tooltip
                                            id="tooltip-7"
                                            text="One-of-a-kind technology providing the exceptional support, pressure relief, and motion cancellation that made Tempur-Pedic® famous."
                                        />
                                    </span>
                                </span>
                            </div>
                        </th>
                        <th scope="col">
                            <span className={styles.headerContainer}>
                                <span>Pressure</span>
                                <span
                                    className={
                                        styles.headerContainerWithTooltip
                                    }
                                >
                                    Relief{" "}
                                    <span
                                        className="tooltip"
                                        data-tooltip="Tempur-Pedic® mattresses gently cradle your body and pressure points to help reduce aches & pains."
                                        title="Tempur-Pedic® mattresses gently cradle your body and pressure points to help reduce aches & pains."
                                    >
                                        <Tooltip
                                            id="tooltip-9"
                                            text="Tempur-Pedic® mattresses gently cradle your body and pressure points to help reduce aches & pains."
                                        />
                                    </span>
                                </span>
                            </span>
                        </th>
                        <th scope="col">
                            <span className={styles.headerContainer}>
                                <span>Profile</span>
                                <span
                                    className={
                                        styles.headerContainerWithTooltip
                                    }
                                >
                                    Height{" "}
                                    <span
                                        className="tooltip"
                                        data-tooltip="A taller profile gives you more TEMPUR® technology for additional pressure relief and conforming support."
                                        title="A taller profile gives you more TEMPUR® technology for additional pressure relief and conforming support."
                                    >
                                        <Tooltip
                                            id="tooltip-0"
                                            text="A taller profile gives you more TEMPUR® technology for additional pressure relief and conforming support."
                                        />
                                    </span>
                                </span>
                            </span>
                        </th>
                        <th scope="col">
                            <div className={styles.headerContainer}>
                                <span>Cover</span>
                                <span className={styles.wordSpace}>
                                    Features
                                </span>
                            </div>
                        </th>
                        <th scope="col">
                            <div className={styles.headerContainer}>
                                <span>Available</span>
                                <span className={styles.wordSpace}>Feels</span>
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <th scope="row">
                            <span className={styles.item}>
                                Adapt<sup>®</sup>
                            </span>
                            <span className={styles.topBg}></span>
                            <span className={styles.middleAdapt}></span>
                            <span className={styles.bottomBg}></span>
                        </th>
                        <td>
                            <span
                                className={styles.tempurTechnologyIcon}
                            ></span>
                            <div className={styles.copyContainer}>
                                <span>TEMPUR</span>
                                <span className={styles.copySub}>
                                    <span>
                                        Material<sup>™</sup>
                                    </span>{" "}
                                    <span
                                        data-tooltip="Infinitely adaptable to your body’s weight, shape and temperature."
                                        title="Infinitely adaptable to your body’s weight, shape and temperature."
                                    >
                                        <Tooltip
                                            id="tooltip-2"
                                            text="Infinitely adaptable to your body’s weight, shape and temperature."
                                        />
                                    </span>
                                </span>
                            </div>
                        </td>
                        <td>
                            <span className={styles.pressureReliefIcon}></span>
                            <div className={styles.copyContainer}>
                                <b>Legendary</b>
                                <span>Pressure Relief</span>
                            </div>
                        </td>
                        <td>
                            <span className={styles.profileHeightIcon}></span>
                            <div className={styles.copyContainer}>
                                <b className={styles.profileHeightNumbers}>
                                    11<sup>&quot;</sup>
                                </b>
                                <span>Profile</span>
                            </div>
                        </td>
                        <td>
                            <span className={styles.coverFeaturesIcon}></span>
                            <div className={styles.copyContainer}>
                                <span>Cool-to-the-Touch</span>
                                <span>Cover</span>
                            </div>
                        </td>
                        <td>
                            <span className={styles.availableFeelsIcon}></span>
                            <div className={styles.listContainer}>
                                <div className={styles.listItem}>
                                    <span className={styles.darkAqua}></span>
                                    <span>Medium</span>
                                </div>
                                <div className={styles.listItem}>
                                    <span className={styles.cornflower}></span>
                                    <div>
                                        <span className={styles.breakWord}>
                                            <span>Medium</span>{" "}
                                            <span>
                                                Hybrid{" "}
                                                <span
                                                    data-tooltip="Medium Hybrid: Over 1,000 premium innersprings complement our one-of-a-kind Tempur-Pedic® pressure-relieving materials to deliver just the right combination of pressure relief and support."
                                                    title="Medium Hybrid: Over 1,000 premium innersprings complement our one-of-a-kind Tempur-Pedic® pressure-relieving materials to deliver just the right combination of pressure relief and support."
                                                >
                                                    <Tooltip
                                                        id="tooltip-6"
                                                        text="Medium Hybrid: Over 1,000 premium innersprings complement our one-of-a-kind Tempur-Pedic® pressure-relieving materials to deliver just the right combination of pressure relief and support."
                                                    />
                                                </span>
                                            </span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <th scope="row">
                            <span className={styles.item}>
                                <span>Pro</span>Adapt<sup>®</sup>
                            </span>
                            <span className={styles.topBg}></span>
                            <span className={styles.middleAdapt2}></span>
                            <span className={styles.bottomBg}></span>
                        </th>
                        <td>
                            <span
                                className={styles.tempurTechnologyIcon2}
                            ></span>
                            <div className={styles.copyContainer}>
                                <span className="nowrap">
                                    Advanced Relief<sup>™</sup>
                                </span>
                                <span className={styles.copySub}>
                                    <span>Material</span>{" "}
                                    <span
                                        data-tooltip="Our most pressure-relieving material ever, providing truly-advanced pressure relief."
                                        title="Our most pressure-relieving material ever, providing truly-advanced pressure relief."
                                    >
                                        <Tooltip
                                            id="tooltip-5"
                                            text="Our most pressure-relieving material ever, providing truly-advanced pressure relief."
                                        />
                                    </span>
                                </span>
                            </div>
                        </td>
                        <td>
                            <span className={styles.pressureReliefIcon2}></span>
                            <div className={styles.copyContainer}>
                                <div>
                                    <b>Advanced</b>
                                </div>
                                <span>up to 20% More</span>
                                <span>
                                    Pressure Relief <sup>#</sup>
                                </span>
                            </div>
                        </td>
                        <td>
                            <span className={styles.profileHeightIcon2}></span>
                            <div className={styles.copyContainer}>
                                <b className={styles.profileHeightNumbers}>
                                    12<sup>&quot;</sup>
                                </b>
                                <span>Profile</span>
                            </div>
                        </td>
                        <td>
                            <span className={styles.coverFeaturesIcon2}></span>
                            <div className={styles.copyContainer}>
                                <span className={styles.wrapSwitch}>
                                    Cool-to-the-Touch,
                                </span>
                                <span>Removable &</span>
                                <span>Washable Cover</span>
                            </div>
                        </td>
                        <td>
                            <span className={styles.availableFeelsIcon2}></span>
                            <div className={styles.listContainer}>
                                <div className={styles.listItem}>
                                    <span className={styles.colorBlue}></span>
                                    <span>Soft</span>
                                </div>
                                <div className={styles.listItem}>
                                    <span className={styles.darkAqua}></span>
                                    <span>Medium</span>
                                </div>
                                <div className={styles.listItem}>
                                    <span className={styles.cornflower}></span>
                                    <div>
                                        <span className={styles.breakWord}>
                                            <span>Medium</span>{" "}
                                            <span>
                                                Hybrid{" "}
                                                <span
                                                    data-tooltip="Medium Hybrid: Over 1,000 premium innersprings complement our one-of-a-kind Tempur-Pedic® pressure-relieving materials to deliver just the right combination of pressure relief and support."
                                                    title="Medium Hybrid: Over 1,000 premium innersprings complement our one-of-a-kind Tempur-Pedic® pressure-relieving materials to deliver just the right combination of pressure relief and support."
                                                >
                                                    <Tooltip
                                                        id="tooltip-3"
                                                        text="Medium Hybrid: Over 1,000 premium innersprings complement our one-of-a-kind Tempur-Pedic® pressure-relieving materials to deliver just the right combination of pressure relief and support."
                                                    />
                                                </span>
                                            </span>
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <div className={styles.listItem}>
                                        <span
                                            className={styles.blazeOrange}
                                        ></span>
                                        <span>Firm</span>
                                    </div>
                                </div>
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <th scope="row">
                            <span className={styles.item}>
                                <span>Luxe</span>Adapt<sup>®</sup>
                            </span>
                            <span className={styles.topBg}></span>
                            <span className={styles.middleAdapt3}></span>
                            <span className={styles.bottomBg}></span>
                        </th>
                        <td>
                            <span
                                className={styles.tempurTechnologyIcon3}
                            ></span>
                            <div className={styles.copyContainer}>
                                <span>Ergonomic</span>
                                <span className={styles.copySub}>
                                    <span>Support Layer</span>{" "}
                                    <span
                                        data-tooltip="A 5-zone layer that precisely contours to your body for maximum ergonmic support."
                                        title="A 5-zone layer that precisely contours to your body for maximum ergonmic support."
                                    >
                                        <Tooltip
                                            id="tooltip-8"
                                            text="A 5-zone layer that precisely contours to your body for maximum ergonmic support."
                                        />
                                    </span>
                                </span>
                            </div>
                        </td>
                        <td>
                            <span className={styles.pressureReliefIcon3}></span>
                            <div className={styles.copyContainer}>
                                <span>
                                    <b>Ultimate</b>
                                </span>
                                <span>up to 30% More</span>
                                <span>
                                    Conforming Support <sup>†</sup>
                                </span>
                            </div>
                        </td>
                        <td>
                            <span className={styles.profileHeightIcon3}></span>
                            <div className={styles.copyContainer}>
                                <b className={styles.profileHeightNumbers}>
                                    13<sup>&quot;</sup>
                                </b>
                                <span>Profile</span>
                            </div>
                        </td>
                        <td>
                            <span className={styles.coverFeaturesIcon3}></span>
                            <div className={styles.copyContainer}>
                                <span>Cool-to-the-Touch,</span>
                                <span>Removable &</span>
                                <span>Washable Cover</span>
                            </div>
                        </td>
                        <td>
                            <span className={styles.availableFeelsIcon3}></span>
                            <div className={styles.listContainer}>
                                <div className={styles.listItem}>
                                    <span className={styles.colorBlue}></span>
                                    <span>Soft</span>
                                </div>
                                <div className={styles.listItem}>
                                    <span className={styles.cornflower}></span>
                                    <div>
                                        <span className={styles.breakWord}>
                                            <span>Medium</span>{" "}
                                            <span>
                                                Hybrid{" "}
                                                <span
                                                    data-tooltip="Medium Hybrid: Over 1,300 premium, double-stacked innersprings and a proprietary hybrid material — for a lighter, more-responsive feel that helps support any sleep position."
                                                    title="Medium Hybrid: Over 1,300 premium, double-stacked innersprings and a proprietary hybrid material — for a lighter, more-responsive feel that helps support any sleep position."
                                                >
                                                    <Tooltip
                                                        id="tooltip-4"
                                                        text="Medium Hybrid: Over 1,300 premium, double-stacked innersprings and a proprietary hybrid material — for a lighter, more-responsive feel that helps support any sleep position."
                                                    />
                                                </span>
                                            </span>
                                        </span>
                                    </div>
                                </div>
                                <div className={styles.listItem}>
                                    <span className={styles.blazeOrange}></span>
                                    <span>Firm</span>
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
);
